import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiFetch } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

function KpiCard({ title, value, subtitle, color = '#1565c0' }) {
  return (
    <Card sx={{ borderRadius: 2, height: '100%', borderTop: `3px solid ${color}` }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color }}>
          {value ?? '—'}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function JuridicoDashboardPage() {
  const { unidadeSelecionada } = useAuth();
  const [filtros, setFiltros] = useState({ de: '', ate: '', orgaoId: '', categoriaAssuntoId: '' });
  const [orgaos, setOrgaos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [graficos, setGraficos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [rO, rC] = await Promise.all([
          apiFetch('/api/juridico/orgaos-emissores'),
          apiFetch('/api/juridico/categorias-assunto'),
        ]);
        const dO = await rO.json().catch(() => []);
        const dC = await rC.json().catch(() => []);
        if (rO.ok) setOrgaos(Array.isArray(dO) ? dO : []);
        if (rC.ok) setCategorias(Array.isArray(dC) ? dC : []);
      } catch (e) {
        console.warn(e);
      }
    })();
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const params = new URLSearchParams();
      Object.entries(filtros).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });
      const qs = params.toString() ? `?${params}` : '';
      const [rk, rg] = await Promise.all([
        apiFetch(`/api/juridico/dashboard/kpis${qs}`),
        apiFetch(`/api/juridico/dashboard/graficos${qs}`),
      ]);
      const dk = await rk.json().catch(() => ({}));
      const dg = await rg.json().catch(() => ({}));
      if (!rk.ok) throw new Error(dk.error || 'Erro KPIs');
      if (!rg.ok) throw new Error(dg.error || 'Erro gráficos');
      setKpis(dk);
      setGraficos(dg);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar dashboard');
      setKpis(null);
      setGraficos(null);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    carregar();
  }, [carregar, unidadeSelecionada?.codigo]);

  useEffect(() => {
    const onRefresh = () => carregar();
    window.addEventListener('refreshPage', onRefresh);
    return () => window.removeEventListener('refreshPage', onRefresh);
  }, [carregar]);

  return (
    <Box sx={{ p: { xs: 1, md: 0.5 } }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
        Dashboard Jurídico
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Unidade: {unidadeSelecionada?.nome || unidadeSelecionada?.codigo || '—'}
      </Typography>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="De"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filtros.de}
              onChange={(e) => setFiltros((p) => ({ ...p, de: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Até"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filtros.ate}
              onChange={(e) => setFiltros((p) => ({ ...p, ate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Órgão</InputLabel>
              <Select
                label="Órgão"
                value={filtros.orgaoId}
                onChange={(e) => setFiltros((p) => ({ ...p, orgaoId: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {orgaos.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>
                    {o.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                label="Categoria"
                value={filtros.categoriaAssuntoId}
                onChange={(e) => setFiltros((p) => ({ ...p, categoriaAssuntoId: e.target.value }))}
              >
                <MenuItem value="">Todas</MenuItem>
                {categorias.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && kpis && (
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard title="Total no período" value={kpis.total} color="#1565c0" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="% no prazo (fechados)"
                value={kpis.pctNoPrazo != null ? `${kpis.pctNoPrazo}%` : '—'}
                subtitle={`Cumpridos: ${kpis.cumpridoPrazo || 0}`}
                color="#2e7d32"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="% com atraso (fechados)"
                value={kpis.pctComAtraso != null ? `${kpis.pctComAtraso}%` : '—'}
                subtitle={`Com atraso: ${kpis.cumpridoAtraso || 0}`}
                color="#ed6c02"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Tempo médio resposta"
                value={kpis.tempoMedioRespostaDias != null ? `${kpis.tempoMedioRespostaDias}d` : '—'}
                color="#00897b"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard title="Vencidos (abertos)" value={kpis.vencidosAbertos} color="#d32f2f" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard title="Vence em 48h" value={kpis.vence48h} color="#f57c00" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard title="Vence em 7 dias" value={kpis.vence7d} color="#fbc02d" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard title="Em aberto" value={kpis.abertos} color="#5c6bc0" />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2, height: 320 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Ofícios por mês
                </Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={graficos?.porMes || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="qtd" name="Qtd" fill="#1565c0" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2, height: 320 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Por órgão emissor
                </Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={graficos?.porOrgao || []} layout="vertical" margin={{ left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="nome" width={110} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="qtd" name="Qtd" fill="#00897b" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2, height: 320 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Assuntos recorrentes
                </Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart data={graficos?.porCategoria || []} layout="vertical" margin={{ left: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="nome" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="qtd" name="Qtd" fill="#5c6bc0" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, borderRadius: 2, height: 320 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Prazo cumprido x atraso (encerrados)
                </Typography>
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={graficos?.prazoAoLongoDoTempo || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="noPrazo" name="No prazo" stroke="#2e7d32" strokeWidth={2} />
                    <Line type="monotone" dataKey="comAtraso" name="Com atraso" stroke="#d32f2f" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            {(graficos?.atrasosDestinatarios || []).length > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, borderRadius: 2, height: 300 }}>
                  <Typography fontWeight={700} sx={{ mb: 1 }}>
                    Destinatários / setores com mais atrasos
                  </Typography>
                  <ResponsiveContainer width="100%" height="90%">
                    <BarChart data={graficos.atrasosDestinatarios} layout="vertical" margin={{ left: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="nome" width={140} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="qtd" name="Atrasos" fill="#d32f2f" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Stack>
      )}
    </Box>
  );
}
